import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Profile, UserRole } from '@/types/database';

interface AuthState {
  // User profile from database
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearAuth: () => void;

  // Computed
  isAdmin: () => boolean;
  isLandlord: () => boolean;
  hasRole: (role: UserRole) => boolean;
  canCreateProperty: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      profile: null,
      isLoading: true,
      error: null,

      setProfile: (profile) => set({ profile, isLoading: false, error: null }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error, isLoading: false }),
      clearAuth: () => set({ profile: null, isLoading: false, error: null }),

      // Role checks
      isAdmin: () => get().profile?.role === 'admin',
      isLandlord: () => get().profile?.role === 'landlord' || get().profile?.role === 'admin',
      hasRole: (role) => get().profile?.role === role,

      // Subscription checks
      canCreateProperty: () => {
        const profile = get().profile;
        if (!profile) return false;
        if (profile.role === 'admin') return true;

        // Count check would happen on server, this is just UI hint
        return profile.subscription_status === 'active' || profile.subscription_plan === 'free';
      },
    }),
    {
      name: 'bookiapp-auth',
      partialize: (state) => ({
        // Only persist non-sensitive data
        profile: state.profile
          ? {
              id: state.profile.id,
              role: state.profile.role,
              subscription_status: state.profile.subscription_status,
              subscription_plan: state.profile.subscription_plan,
            }
          : null,
      }),
    }
  )
);

export default useAuthStore;
